import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import { postAuthRedirect } from "lib/auth/authClient";
import { isPersonalEmailDomain } from "lib/emailUtils";
import { useChangeEmail } from "lib/hooks/api/auth/useChangeEmail";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { FieldContainer } from "./utils";

/** Where the requester's browser remembers the change it's waiting on. Also cleared on completion. */
export const PENDING_EMAIL_CHANGE_STORAGE_KEY = "pendingEmailChange";

// Refusals from auth-service's email-change rules, for the request and for either link.
export const CHANGE_EMAIL_REQUEST_ERRORS: Record<string, string> = {
  work_email_required: "Your new email needs to be a work email address.",
  email_outside_company:
    "Your new email needs to be at the same company as your current one. Moving to a new company? Sign up with your new work email instead.",
};

const DEFAULT_CHANGE_EMAIL_REQUEST_ERROR =
  "We couldn't start your email change. Please try again, or contact us if the problem persists.";

/** Copy for a refused email-change request. Unknown codes fall back to a generic message. */
function changeEmailRequestErrorText(error: string | undefined): string {
  return (
    (error && CHANGE_EMAIL_REQUEST_ERRORS[error.toLowerCase()]) ||
    DEFAULT_CHANGE_EMAIL_REQUEST_ERROR
  );
}

function PendingBanner({
  currentEmail,
  pendingEmail,
  onReset,
}: {
  currentEmail: string;
  pendingEmail: string;
  onReset: () => void;
}) {
  return (
    <Flex column gap={16}>
      <Alert type="info">
        We've sent a confirmation email to <strong>{currentEmail}</strong>. Please check your inbox
        to approve the change to <strong>{pendingEmail}</strong>.
        <br />
        Once approved, we'll send a second email to <strong>{pendingEmail}</strong>. You'll need to
        click the link in that one too to finish the change.
      </Alert>
      <Text description color="sysTextSecondary">
        Lost access to your old email? <a href="mailto:team@stotles.com">Contact support</a> for
        help.
      </Text>
      <Button buttonType="secondary" width="fit-content" onClick={onReset}>
        Wrong new email? Try again
      </Button>
    </Flex>
  );
}

function ChangeEmailForm({
  currentEmail,
  onPendingEmailSet,
}: {
  currentEmail: string;
  onPendingEmailSet: (email: string) => void;
}) {
  const schema = z.object({
    newEmail: z
      .email("Enter a valid email address")
      .min(1, "Email is required")
      .refine((val) => val.toLowerCase() !== currentEmail.toLowerCase(), {
        message: "New email must be different from your current email",
      })
      .refine((val) => !isPersonalEmailDomain(val), {
        message: CHANGE_EMAIL_REQUEST_ERRORS.work_email_required,
      }),
  });
  type ChangeEmailFormValues = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<ChangeEmailFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newEmail: "" },
  });

  const newEmail = watch("newEmail");

  const { mutate: changeEmail, isLoading } = useChangeEmail({
    onSuccess: (_, variables) => {
      onPendingEmailSet(variables.newEmail);
    },
    onError: (error) => {
      setError("newEmail", { message: changeEmailRequestErrorText(error.code) });
    },
  });

  const onSubmit = (values: ChangeEmailFormValues) => {
    changeEmail({
      newEmail: values.newEmail,
      // A public page: the links work without being signed in, so their result can't need a session.
      callbackURL: postAuthRedirect("/auth/email-change"),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex column gap={16}>
        <Flex column gap={4}>
          <Text bold>Current email</Text>
          <Text color="sysTextSecondary">{currentEmail}</Text>
        </Flex>
        <FieldContainer>
          <Label htmlFor="newEmail">New email</Label>
          <Input
            control={control}
            name="newEmail"
            type="email"
            autoComplete="email"
            placeholder="Enter your new email address"
          />
          {errors.newEmail && (
            <Text description color="sysDestructiveDefault">
              {errors.newEmail.message}
            </Text>
          )}
        </FieldContainer>
        <div>
          <Button buttonType="primary" type="submit" loading={isLoading} disabled={!newEmail}>
            Update email
          </Button>
        </div>
      </Flex>
    </form>
  );
}

export type PendingEmailChange = {
  email: string;
  expiresAt: number;
};

/**
 * The requester's side of an email change: the form, then "check your inbox" until it completes.
 * What happens when a link is followed is shown on /auth/email-change (BetterAuthEmailChangePage),
 * which works on any device and without a session.
 */
function ChangeEmail(): JSX.Element {
  const currentUser = useAssertCurrentUser();
  const [stored, setStored] = useLocalStorage<PendingEmailChange | null>(
    PENDING_EMAIL_CHANGE_STORAGE_KEY,
    null,
  );

  const isExpired = !!stored && stored.expiresAt <= Date.now();
  // Finished elsewhere (e.g. on another device), so the user already has the address.
  const isComplete =
    !!stored && !isExpired && stored.email.toLowerCase() === currentUser.email.toLowerCase();
  const pendingEmail = stored && !isExpired && !isComplete ? stored.email : null;

  // Kept after the stored change is cleared, so the success message outlives it.
  const [showSuccess, setShowSuccess] = useState(false);
  useEffect(() => {
    if (isComplete) setShowSuccess(true);
    if (isExpired || isComplete) setStored(null);
  }, [isExpired, isComplete, setStored]);

  const clearPending = () => setStored(null);
  const savePending = (email: string) =>
    setStored({
      email,
      expiresAt: Date.now() + 2 * 60 * 60 * 1000, // 2 hours
    });

  if (pendingEmail) {
    return (
      <PendingBanner
        currentEmail={currentUser.email}
        pendingEmail={pendingEmail}
        onReset={clearPending}
      />
    );
  }

  return (
    <Flex column gap={16}>
      {showSuccess && (
        <Alert type="info">
          Your email address has been changed to <strong>{currentUser.email}</strong>.
        </Alert>
      )}
      <ChangeEmailForm currentEmail={currentUser.email} onPendingEmailSet={savePending} />
    </Flex>
  );
}

export default ChangeEmail;
